import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { collection, query, where, getDocs, addDoc, serverTimestamp, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Badge } from '../components/ui';
import { UserProfile } from '../types';

const TOPIC_OPTIONS = [
  { id: 'court', label: 'Family Court Process', icon: '🏛️' },
  { id: 'alienation', label: 'Parental Alienation', icon: '💔' },
  { id: 'contact', label: 'Blocked Contact', icon: '🚫' },
  { id: 'safeguarding', label: 'Safeguarding & SS', icon: '🚩' },
  { id: 'mediation', label: 'Mediation / MIAM', icon: '🤝' },
  { id: 'finances', label: 'Finances & Maintenance', icon: '💰' },
];

const BuddyMatcher = () => {
    const { userProfile } = useAuth();
    const navigate = useNavigate();
    
    const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
    const [buddies, setBuddies] = useState<UserProfile[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isRequesting, setIsRequesting] = useState<string | null>(null);

    const [showWaitlistForm, setShowWaitlistForm] = useState(false);
    const [waitlistContext, setWaitlistContext] = useState('');
    const [isSubmittingWaitlist, setIsSubmittingWaitlist] = useState(false);

    useEffect(() => {
        if (selectedTopics.length === 0) {
            setBuddies([]);
            setShowWaitlistForm(false);
            return;
        }

        const fetchBuddies = async () => {
            setIsLoading(true);
            setShowWaitlistForm(false);
            try {
                // Query active buddies
                const q = query(
                    collection(db, 'users'),
                    where('isBuddy', '==', true),
                    where('buddyProfile.status', '==', 'active'),
                    where('buddyProfile.topics', 'array-contains-any', selectedTopics)
                );
                
                const snapshot = await getDocs(q);
                const fetchedBuddies: UserProfile[] = [];
                snapshot.forEach(doc => {
                    const data = doc.data() as UserProfile;
                    // Don't show yourself
                    if (data.uid !== userProfile?.uid) {
                        fetchedBuddies.push(data);
                    }
                });
                
                setBuddies(fetchedBuddies);
            } catch (err) {
                console.error("Error fetching buddies:", err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchBuddies();
    }, [selectedTopics, userProfile?.uid]);

    const toggleTopic = (topicId: string) => {
        setSelectedTopics(prev => prev.includes(topicId) 
            ? prev.filter(t => t !== topicId) 
            : [...prev, topicId]);
    };

    const handleJoinWaitlist = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!userProfile) return;
        setIsSubmittingWaitlist(true);

        try {
            console.log("Saving support request...");
            await addDoc(collection(db, 'support_requests'), {
                userId: userProfile.uid,
                userName: userProfile.nickname || userProfile.displayName,
                email: userProfile.email || '',
                topics: selectedTopics,
                context: waitlistContext,
                status: 'pending',
                createdAt: serverTimestamp()
            });
        } catch (e) {
            console.error("Waitlist error adding to support_requests:", e);
            alert("Failed to create waitlist request.");
            setIsSubmittingWaitlist(false);
            return;
        }

        try {
            console.log("Updating user profile needsSupport flag...");
            await updateDoc(doc(db, 'users', userProfile.uid), {
                needsSupport: true
            });
        } catch (e) {
            console.error("Waitlist error updating users collection:", e);
            alert("Failed to flag user account. Waitlist request completed, but admin indicator may not show.");
        }

        // Calculate Temp Checkin Metrics
        let latestTempVal = 'No check-ins';
        let latestTempWord = '';
        let avgTempVal = 'N/A';
        
        const getZoneWord = (val: number) => {
            if (val <= -4) return "Checked out";
            if (val <= -1) return "Distant";
            if (val === 0) return "Present";
            if (val <= 3) return "Stressed";
            return "Boiling";
        };

        let isUrgentRisk = false;
        let fluctuationVal = 'N/A';
        if (userProfile.thermalCheckins && userProfile.thermalCheckins.length > 0) {
            const sortedCheckins = [...userProfile.thermalCheckins].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
            const latestCheckin = sortedCheckins[0];
            const latest = latestCheckin.value;
            latestTempVal = latest > 0 ? `+${latest}` : `${latest}`;
            latestTempWord = latestCheckin.compositeLabel ? ` (${latestCheckin.compositeLabel})` : ` (${getZoneWord(latest)})`;
            
            // Mark as urgent if in the extreme zones or if deep negative mood (Valence <= -3)
            const hasNegativeMood = latestCheckin.points?.some(p => p.x <= -3);
            if (latest >= 4 || latest <= -4 || hasNegativeMood) {
                isUrgentRisk = true;
            }

            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
            
            const recentCheckins = sortedCheckins.filter(c => new Date(c.timestamp) >= sevenDaysAgo);
            if (recentCheckins.length > 0) {
                let minMood = recentCheckins[0].points?.[0]?.x ?? recentCheckins[0].value;
                let maxMood = minMood;

                const sum = recentCheckins.reduce((acc, curr) => {
                    const moodVal = curr.points?.[0]?.x ?? curr.value;
                    if (moodVal < minMood) minMood = moodVal;
                    if (moodVal > maxMood) maxMood = moodVal;
                    return acc + curr.value;
                }, 0);
                
                const avg = Math.round((sum / recentCheckins.length) * 10) / 10;
                avgTempVal = avg > 0 ? `+${avg}` : `${avg}`;

                // Detect massive mood swings (Valence fluctuation)
                const gap = Math.abs(maxMood - minMood);
                if (recentCheckins.length > 1) {
                    fluctuationVal = `${gap} point mood swing`;
                    if (gap >= 6) {
                        isUrgentRisk = true;
                        if (!latestTempWord.includes('⚠️')) {
                            latestTempWord += ' ⚠️ (URGENT MOOD SWING DETECTED)';
                        }
                    }
                }
            }
        }

        const urgencyLabel = isUrgentRisk ? '❌ [URGENT RISK] ' : '';
        const metricsText = `\n\n--- Thermal Checkin Metrics ---\nLatest Temp: ${latestTempVal}${latestTempWord}\n7-Day Average: ${avgTempVal}\n7-Day Fluctuation: ${fluctuationVal}`;
        const metricsHtml = `<div style="background-color: ${isUrgentRisk ? '#fef2f2' : '#f8fafc'}; padding: 15px; border-radius: 8px; margin: 20px 0; border: 1px solid ${isUrgentRisk ? '#fecaca' : '#e2e8f0'};">
                                <h3 style="margin-top: 0; color: ${isUrgentRisk ? '#991b1b' : '#0f172a'}; font-size: 16px;">${isUrgentRisk ? '🚨 URGENT: High-Risk ' : ''}Thermal Checkin Metrics</h3>
                                <p style="margin: 5px 0; color: ${isUrgentRisk ? '#b91c1c' : '#333'}; font-weight: ${isUrgentRisk ? 'bold' : 'normal'};"><strong>Latest Temp:</strong> ${latestTempVal}${latestTempWord}</p>
                                <p style="margin: 5px 0;"><strong>7-Day Average:</strong> ${avgTempVal}</p>
                                <p style="margin: 5px 0; color: ${fluctuationVal.includes('point') && parseInt(fluctuationVal) >= 6 ? '#b91c1c' : '#333'}; font-weight: ${fluctuationVal.includes('point') && parseInt(fluctuationVal) >= 6 ? 'bold' : 'normal'};"><strong>7-Day Fluctuation:</strong> ${fluctuationVal}</p>
                             </div>`;

        try {
            console.log("Sending admin email notification...");
            await addDoc(collection(db, 'mail'), {
                to: ['info@dadsmatter.org.uk'],
                message: {
                    subject: `${urgencyLabel}New Buddy Waitlist Request`,
                    text: `A user has joined the buddy waitlist.\n\nUser: ${userProfile.displayName}\nTopics: ${selectedTopics.join(', ')}\nContext:\n${waitlistContext}${metricsText}`,
                    html: `<p>A user has joined the buddy waitlist.</p><p><strong>User:</strong> ${userProfile.displayName}</p><p><strong>Topics:</strong> ${selectedTopics.join(', ')}</p><p><strong>Context:</strong><br/>${waitlistContext}</p>${metricsHtml}`,
                    ...(isUrgentRisk ? { 
                        priority: 'high', 
                        headers: { 
                            'Importance': 'high', 
                            'X-Priority': '1', 
                            'X-MSMail-Priority': 'High' 
                        } 
                    } : {})
                }
            });
        } catch (e) {
            console.error("Waitlist error adding to mail collection:", e);
        }

        try {
            if (userProfile.email) {
                console.log("Sending user confirmation email...");
                await addDoc(collection(db, 'mail'), {
                    to: [userProfile.email],
                    message: {
                        subject: 'We have received your request for support',
                        text: `Hi ${userProfile.nickname || userProfile.displayName},\n\nThe Dads Matter team have received your request for support and are actively looking for a Buddy to assist you.\n\nIf you don't hear from us in 48 hours, please feel free to drop us an email at info@dadsmatter.org.uk.\n\nThank you,\nThe Dads Matter Team`,
                        html: `<div style="font-family: sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #eee; border-radius: 10px;">
                                <h2 style="color: #10b981; text-align: center;">Support Request Received 🤝</h2>
                                <p style="font-size: 16px; color: #333; line-height: 1.6;">Hi ${userProfile.nickname || userProfile.displayName},</p>
                                <p style="font-size: 16px; color: #333; line-height: 1.6;">The Dads Matter team have your request for support and are actively looking for a Buddy to assist you.</p>
                                <p style="font-size: 16px; color: #333; line-height: 1.6;">If you don't hear from us in 48 hours, please feel free to drop us an email at <a href="mailto:info@dadsmatter.org.uk" style="color: #10b981; text-decoration: none; font-weight: bold;">info@dadsmatter.org.uk</a>.</p>
                                <hr style="margin: 30px 0; border: none; border-top: 1px solid #eee;" />
                                <p style="font-size: 12px; color: #999; text-align: center;">The Dads Matter Team</p>
                               </div>`
                    }
                });
            }
        } catch (e) {
            console.error("Waitlist error sending user confirmation email:", e);
        }

        alert("You have been added to the waitlist. Our team will contact you soon.");
        setShowWaitlistForm(false);
        setWaitlistContext('');
        setIsSubmittingWaitlist(false);
    };

    const handleRequestSupport = async (buddy: UserProfile) => {
        if (!userProfile) return;
        setIsRequesting(buddy.uid);
        
        try {
            // Check if connection already exists
            const q = query(
                collection(db, 'connections'),
                where('users', 'array-contains', userProfile.uid)
            );
            const snap = await getDocs(q);
            const existingConn = snap.docs.find(d => d.data().users.includes(buddy.uid));

            if (!existingConn) {
                // Create new pending connection
                await addDoc(collection(db, 'connections'), {
                    users: [userProfile.uid, buddy.uid],
                    requesterUid: userProfile.uid,
                    receiverUid: buddy.uid,
                    status: 'pending',
                    updatedAt: serverTimestamp(),
                    isBuddyRequest: true // Flag to indicate this came from the Buddy system
                });

                if (buddy.email) {
                    const messageLink = `${window.location.origin}/#/messages`;
                    // Just list all matched topics
                    const matchedTopicsLabel = TOPIC_OPTIONS.filter(t => selectedTopics.includes(t.id)).map(t => t.label).join(', ');
                    
                    await addDoc(collection(db, 'mail'), {
                        to: buddy.email,
                        message: {
                            subject: "New Buddy Support Request!",
                            text: `Hi ${buddy.nickname || buddy.displayName},\n\nA member of the Dads Matter Hub (@${userProfile.nickname || userProfile.displayName}) has requested your peer support as a Buddy regarding: ${matchedTopicsLabel}.\n\nPlease log in to check your pending message requests:\n${messageLink}\n\nThank you,\nThe Dads Matter Team`,
                            html: `<div style="font-family: sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #eee; border-radius: 10px;">
                                    <h2 style="color: #10b981; text-align: center;">New Support Request! 🤝</h2>
                                    <p style="font-size: 16px; color: #333; line-height: 1.6;">Hi ${buddy.nickname || buddy.displayName},</p>
                                    <p style="font-size: 16px; color: #333; line-height: 1.6;">Someone navigating the Dads Matter Hub (<strong>@${userProfile.nickname || userProfile.displayName}</strong>) read your profile and has requested your peer support as a Buddy.</p>
                                    <p style="font-size: 16px; color: #333; line-height: 1.6;"><strong>Support Topics:</strong> ${matchedTopicsLabel}</p>
                                    <div style="text-align: center; margin: 30px 0;">
                                        <a href="${messageLink}" style="background-color: #10b981; color: white; padding: 15px 30px; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 16px; display: inline-block;">View Pending Request</a>
                                    </div>
                                    <p style="font-size: 16px; color: #333; line-height: 1.6;">Thank you for your willingness to help!</p>
                                    <hr style="margin: 30px 0; border: none; border-top: 1px solid #eee;" />
                                    <p style="font-size: 12px; color: #999; text-align: center;">The Dads Matter Team</p>
                                   </div>`
                        }
                    });
                }
            }

            // Navigate to messages
            navigate('/messages');
        } catch (err) {
            console.error("Support request failed:", err);
            alert("Failed to send request. Please try again.");
            setIsRequesting(null);
        }
    };

    return (
        <Layout>
            <div className="max-w-4xl mx-auto py-8">
                <div className="text-center mb-10 animate-slide-up">
                    <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center text-4xl mx-auto mb-4">🤝</div>
                    <h1 className="text-3xl font-heading font-bold text-slate-900 mb-2">Dads Matter Buddies</h1>
                    <p className="text-slate-500 text-lg">Connect with experienced dads who have walked the same path.</p>
                </div>

                <div className="mb-10 animate-slide-up delay-100">
                    <h2 className="text-lg font-bold text-slate-800 mb-2 px-2">What do you need support with right now?</h2>
                    <p className="text-sm text-slate-500 mb-4 px-2">You can select multiple topics.</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {TOPIC_OPTIONS.map(topic => (
                            <button
                                key={topic.id}
                                onClick={() => toggleTopic(topic.id)}
                                className={`p-4 rounded-xl border-2 text-left transition-all ${selectedTopics.includes(topic.id) ? 'border-emerald-500 bg-emerald-50 shadow-md transform -translate-y-1' : 'border-slate-200 bg-white hover:border-emerald-300 hover:shadow-sm'}`}
                            >
                                <div className="text-2xl mb-2">{topic.icon}</div>
                                <div className={`font-bold text-sm ${selectedTopics.includes(topic.id) ? 'text-emerald-900' : 'text-slate-700'}`}>{topic.label}</div>
                            </button>
                        ))}
                    </div>
                </div>

                {selectedTopics.length > 0 && (
                    <div className="animate-fade-in border-t border-slate-200 pt-8">
                        <div className="flex justify-between items-end mb-6 px-2">
                            <h2 className="text-2xl font-bold text-slate-900">
                                Match Results
                            </h2>
                            <span className="text-sm font-bold text-slate-500">{buddies.length} Buddies Found</span>
                        </div>

                        {isLoading ? (
                            <div className="py-20 text-center">
                                <div className="inline-block w-8 h-8 rounded-full border-4 border-emerald-500 border-t-transparent animate-spin mb-4"></div>
                                <p className="text-slate-500 font-bold animate-pulse">Finding appropriate matches...</p>
                            </div>
                        ) : buddies.length === 0 ? (
                            <div className="space-y-6">
                                <Card className="p-12 text-center border-dashed border-2 bg-slate-50">
                                    <div className="text-4xl mb-4">🏜️</div>
                                    <h3 className="text-lg font-bold text-slate-800 mb-2">No buddies currently available</h3>
                                    <p className="text-slate-500 text-sm max-w-md mx-auto mb-6">All of our buddies specializing in these areas might be at capacity right now, or we do not currently have matches for your specific selection.</p>
                                    {!showWaitlistForm ? (
                                        <Button 
                                            onClick={() => setShowWaitlistForm(true)}
                                            className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-indigo-200 font-bold !px-6"
                                        >
                                            Add me to the Buddy Waitlist
                                        </Button>
                                    ) : (
                                        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm text-left max-w-xl mx-auto animate-fade-in">
                                            <h4 className="font-bold text-slate-900 mb-2">Join the Waitlist</h4>
                                            <p className="text-xs text-slate-500 mb-4">Please provide some context about your needs so we can match you as soon as someone becomes available.</p>
                                            <form onSubmit={handleJoinWaitlist}>
                                                <textarea
                                                    value={waitlistContext}
                                                    onChange={(e) => setWaitlistContext(e.target.value)}
                                                    required
                                                    rows={4}
                                                    placeholder="E.g. I have a court hearing in 3 weeks regarding contact and need someone who has navigated this before..."
                                                    className="w-full border-slate-200 rounded-lg p-3 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 mb-4"
                                                />
                                                <div className="flex gap-3 justify-end">
                                                    <Button 
                                                        type="button" 
                                                        variant="outline" 
                                                        onClick={() => setShowWaitlistForm(false)}
                                                    >
                                                        Cancel
                                                    </Button>
                                                    <Button 
                                                        type="submit" 
                                                        disabled={isSubmittingWaitlist || !waitlistContext.trim()}
                                                        className="bg-indigo-600 hover:bg-indigo-700 text-white"
                                                    >
                                                        {isSubmittingWaitlist ? 'Submitting...' : 'Submit Request'}
                                                    </Button>
                                                </div>
                                            </form>
                                        </div>
                                    )}
                                </Card>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {buddies.map(buddy => {
                                    const buddyTopics = TOPIC_OPTIONS.filter(t => buddy.buddyProfile?.topics.includes(t.id));
                                    
                                    return (
                                    <Card key={buddy.uid} className="flex flex-col h-full hover:shadow-xl transition-shadow border-slate-200 border-t-4 border-t-emerald-500">
                                        <div className="p-6 flex-1 flex flex-col">
                                            <div className="flex justify-between items-start mb-4">
                                                <div>
                                                    <h3 className="text-xl font-bold text-slate-900">{buddy.nickname || buddy.displayName.split(' ')[0]}</h3>
                                                    <span className="text-xs text-emerald-600 font-bold uppercase tracking-wider">Community Buddy</span>
                                                </div>
                                                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center text-xl shadow-inner border border-slate-200">
                                                    🧔
                                                </div>
                                            </div>
                                            
                                            <div className="bg-slate-50 rounded-xl p-4 mb-4 flex-1 border border-slate-100">
                                                <p className="text-sm text-slate-600 italic line-clamp-4 leading-relaxed">
                                                    "{buddy.buddyProfile?.bio}"
                                                </p>
                                            </div>

                                            <div className="mb-6">
                                                <div className="text-[10px] font-bold text-slate-400 uppercase mb-2">Experience Areas</div>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {buddyTopics.map(t => (
                                                        <span key={t.id} className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 bg-white border border-slate-200 rounded-md text-slate-600 shadow-sm">
                                                            <span>{t.icon}</span> {t.label}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>

                                            <Button 
                                                onClick={() => handleRequestSupport(buddy)}
                                                disabled={isRequesting === buddy.uid}
                                                className="w-full mt-auto bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-200 font-bold !py-3"
                                            >
                                                {isRequesting === buddy.uid ? 'Sending Request...' : 'Send Support Request'}
                                            </Button>
                                        </div>
                                    </Card>
                                )})}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default BuddyMatcher;
